import { useNavigation } from '@/app/hooks'
import { useProgramRequirements } from '@/app/hooks/useProgramRequirements'
import { useAppDispatch, useAppSelector, useMockableViewModel } from '@/hooks'
import {
  completeEnrollment,
  selectInProgressEnrollment,
} from '@/reducers/inProgressUtilityProgramEnrollments'
import { ProfileEligibilityRequirements } from '@/types/programs-new/profile-requirements'
import { ClientRequirements } from '@/types/utilityProgramProfileEligibility'
import { useMemo, useState } from 'react'
import { useMultiProgramRouteContext } from '..'
import { useSelectedUtility } from '@/hooks/useSelectedUtility'
import { logEvent } from '@/logging'
import useMultiProgramFlowControl from '@/hooks/useMultiProgramFlowControl'
import { useCurrentMultiProgramFlowContext } from '@/app/context/MultiProgram/MultiProgramFlowContexts'
import useEnrollmentActions from '@/hooks/useEnrollmentActions'
import {
  programsNewCollection,
  selectExistingEnrollmentProgram,
  selectProgramDevices,
  selectProgramRewardDeviceLimits,
  selectRequiredEnrollmentSurveyResponses,
} from '@/reducers/programsNew'
import getEnrollmentSurveyContent from './enrollment-survey-content/get-enrollment-survey-content'
import useUtilityProgramRewards from '@/app/hooks/useUtilityProgramRewards'
import { SelectedDeviceCounts } from '@/components/ProgramRewards/program-rewards-utils'
import ReactGA from 'react-ga4'
import useRemoteConfig from '@/hooks/useRemoteConfig'

function useEnrollmentAnalytics() {
  const { appRemoteConfig } = useRemoteConfig()

  let enrollmentEventGtags:
    | {
        programId: number
        eventId: string
        label: string
      }[]
    | undefined

  try {
    enrollmentEventGtags = JSON.parse(
      appRemoteConfig?.getString('enrollment_event_gtags') ?? '[]',
    )
  } catch (e) {
    console.error('Error parsing enrollment_event_gtags remote config', e)
  }

  const fireAnalyticEvent = (programId: number) => {
    const gtagData = enrollmentEventGtags?.find(
      (event) => event.programId === programId,
    )
    if (!gtagData) {
      return
    }
    ReactGA.gtag('event', gtagData.eventId, {
      event_category: 'Form',
      event_label: gtagData.label,
    })
  }

  return { fireAnalyticEvent }
}

export function useViewModel() {
  const { utilityProgram } = useMultiProgramRouteContext()
  const { data: programsData } = programsNewCollection.useFetch()
  const dispatch = useAppDispatch()
  const { programRequirements } = useProgramRequirements(utilityProgram)
  const { rewardsByCategory, rewardsByDeviceType } = useUtilityProgramRewards(
    utilityProgram.id,
  )
  const [isSubmitting, setIsSubmitting] = useState(false)
  const { fireAnalyticEvent } = useEnrollmentAnalytics()
  const eligible = programRequirements?.steps
    .filter((s) => s.key !== ClientRequirements.ProgramEnrollment)
    .every((s) => s.completed)
  const navigate = useNavigation()
  const { clearFlow } = useMultiProgramFlowControl()
  const { enrollDevices, logRequestProgramEnrollment } = useEnrollmentActions()
  const inProgressEnrollment = useAppSelector(
    selectInProgressEnrollment(utilityProgram.id),
  )
  const { routePrefix } = useCurrentMultiProgramFlowContext()
  const { data: utility } = useSelectedUtility()
  const programDevices = useAppSelector(selectProgramDevices(utilityProgram.id))
  const existingEnrolledProgram = useAppSelector(
    selectExistingEnrollmentProgram(utilityProgram.id),
  )
  const rewardDeviceLimits = useAppSelector(
    selectProgramRewardDeviceLimits(utilityProgram.id),
  )
  const vehicles = programDevices.vehicles
  const thermostats = programDevices.thermostats
  const chargers = programDevices.chargers
  const selectedDeviceCounts = {
    vehicleCount: inProgressEnrollment?.selectedDevices.vehicleIds.length ?? 0,
    thermostatCount:
      inProgressEnrollment?.selectedDevices.thermostatIds.length ?? 0,
    chargerCount: inProgressEnrollment?.selectedDevices.chargerIds.length ?? 0,
  } satisfies SelectedDeviceCounts
  const selectedDeviceNames = useMemo(() => {
    const hasDevicesReq = programRequirements?.steps.find(
      (s) => s.key === ClientRequirements.SelectDevice,
    )
    if (!hasDevicesReq) {
      return []
    }
    if (!inProgressEnrollment?.selectedDevices) {
      return []
    }
    const vehicleNames = vehicles
      .filter((v) =>
        inProgressEnrollment.selectedDevices.vehicleIds.includes(v?.id),
      )
      .filter((v) => v.car.car_model)
      .map(
        (v) => `(${v.car.car_model?.year}) ${v.car.car_model?.friendly_name}`,
      )
    const thermostatNames = thermostats
      .filter((t) =>
        inProgressEnrollment.selectedDevices.thermostatIds.includes(t?.id),
      )
      .filter((t) => t.name)
      .map((t) => t.name)
    const chargerNames = chargers
      .filter((c) =>
        inProgressEnrollment.selectedDevices.chargerIds.includes(c?.id),
      )
      .map((c) => c.name ?? 'Chargepoint Charger')
    return [...vehicleNames, ...thermostatNames, ...chargerNames]
  }, [programDevices])
  const reviewedTOS = useMemo(() => {
    const hasReviewTOSReq = programRequirements?.steps.find(
      (s) => s.key === ClientRequirements.ReviewTOS,
    )
    if (!hasReviewTOSReq) {
      return false
    }
    return inProgressEnrollment?.tosAccepted ?? false
  }, [inProgressEnrollment, programRequirements])
  const selectedUtility = useMemo(() => {
    const hasSelectedUtilityRequirement = programRequirements?.steps.find(
      (s) => s.key === ProfileEligibilityRequirements.RequireUtility,
    )
    if (!hasSelectedUtilityRequirement) {
      return null
    }
    return utility
  }, [programRequirements])

  const utilityAccountNumber = useMemo(() => {
    const hasUtilityAccountNumberRequirement = programRequirements?.steps.find(
      (s) => s.key === ClientRequirements.EnterUtilityAccountNumber,
    )
    if (!hasUtilityAccountNumberRequirement) {
      return null
    }
    return inProgressEnrollment?.utilityAccountNumber ?? null
  }, [programRequirements, inProgressEnrollment])

  const onEnroll = async () => {
    const chargerIds = inProgressEnrollment?.selectedDevices.chargerIds ?? []
    const vehicleIds = inProgressEnrollment?.selectedDevices.vehicleIds ?? []
    const thermostatIds =
      inProgressEnrollment?.selectedDevices.thermostatIds ?? []

    setIsSubmitting(true)
    logRequestProgramEnrollment({
      vehicles,
      chargers,
      thermostats,
      selections: {
        chargerIds,
        vehicleIds,
        thermostatIds,
        profileIds: [],
      },
      utilityProgramId: utilityProgram.id,
      existingEnrollment:
        existingEnrolledProgram?.enrollment_overview ?? undefined,
    })
    const result = await enrollDevices({
      utilityProgramId: utilityProgram.id,
      vehicleIds,
      thermostatIds,
      chargerIds,
    })
    if (result.isErr()) {
      logEvent(`Error enrolling profile`)
      navigate.push(
        `${routePrefix}/multi-program-enrollment/${utilityProgram.id}/enrollment-state/error`,
      )
      setIsSubmitting(false)
      return
    }

    fireAnalyticEvent(utilityProgram.id)

    dispatch(completeEnrollment(utilityProgram.id))
    clearFlow()
    navigate.push(
      `${routePrefix}/multi-program-enrollment/${utilityProgram.id}/enrollment-state/pending`,
    )
    setIsSubmitting(false)
  }

  const surveys = useAppSelector(
    selectRequiredEnrollmentSurveyResponses(utilityProgram.id),
  )
  const enrollmentSurveyContent = surveys
    .filter((s) => !!s.completed)
    .map((s) => getEnrollmentSurveyContent(s))

  const offPeakChargingLabel =
    utilityProgram.view_config_json?.programCard?.offPeakChargingLabel ??
    undefined

  return {
    selectedDeviceNames,
    selectedUtility,
    reviewedTOS,
    utilityAccountNumber,
    onEnroll,
    eligible,
    programsData,
    isSubmitting,
    enrollmentSurveyContent,
    rewardsByCategory,
    rewardsByDeviceType,
    selectedDeviceCounts,
    rewardDeviceLimits,
    offPeakChargingLabel,
  }
}

function useMockViewModel() {
  return {}
}

export default useMockableViewModel({ useViewModel, useMockViewModel })
